import { makeAutoObservable } from 'mobx';
import { serialize, unserialize } from '../../../store/utils/localstorage';
import { Role } from '../../users/store/User';

export default class AuthSession {
  mainSession: AuthSessionDto;
  impersonatedSession?: AuthSessionDto;

  constructor({ impersonated, ...dto }: FullAuthSessionDto) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.mainSession = {
      idToken: dto.idToken,
      refreshToken: dto.refreshToken,
      role: convertRole(dto.role),
    };

    if (impersonated && typeof impersonated === 'object') {
      this.impersonatedSession = {
        idToken: impersonated.idToken,
        refreshToken: impersonated.refreshToken,
        role: convertRole(impersonated.role),
      };
    }
  }

  static restore(): AuthSession | null {
    const dto = unserialize<AuthSessionDto>('session');
    if (dto?.idToken && dto?.refreshToken) {
      return new AuthSession(dto);
    }

    return null;
  }

  get idToken() {
    return this.impersonatedSession?.idToken || this.mainSession.idToken;
  }

  get isImpersonated(): boolean {
    return Boolean(this.impersonatedSession);
  }

  get refreshToken() {
    return (
      this.impersonatedSession?.refreshToken || this.mainSession.refreshToken
    );
  }

  finishImpersonation() {
    this.impersonatedSession = undefined;
    this.serialize();
  }

  hasRole(role: Role, { mode = 'active' }: HasRoleOptions = {}): boolean {
    const impersonatedHasRole = Boolean(
      this.impersonatedSession &&
      ROLES_INHERITANCE[this.impersonatedSession?.role]?.includes(role),
    );

    const mainHasRole =
      ROLES_INHERITANCE[this.mainSession.role]?.includes(role);

    switch (mode) {
      case 'active': {
        return this.isImpersonated ? impersonatedHasRole : mainHasRole;
      }
      case 'any': {
        return impersonatedHasRole || mainHasRole;
      }
      case 'impersonated': {
        return impersonatedHasRole;
      }
      case 'main': {
        return mainHasRole;
      }
      default: {
        throw new Error(`Unknown role check mode: ${mode}`);
      }
    }
  }

  impersonate({ idToken, refreshToken, role }: { role: string } & TokenDto) {
    this.impersonatedSession = {
      idToken,
      refreshToken,
      role: convertRole(role),
    };
    this.serialize();
  }

  serialize() {
    serialize('session', {
      idToken: this.mainSession.idToken,
      refreshToken: this.mainSession.refreshToken,
      role: this.mainSession.role,
      impersonated: this.impersonatedSession && {
        idToken: this.impersonatedSession.idToken,
        refreshToken: this.impersonatedSession.refreshToken,
        role: this.impersonatedSession.role,
      },
    });
  }

  update({ idToken, refreshToken, role }: { role?: string } & TokenDto) {
    const session = this.impersonatedSession || this.mainSession;

    session.idToken = idToken;
    session.refreshToken = refreshToken;

    if (role) {
      session.role = convertRole(role);
    }

    this.serialize();
  }
}

function convertRole(dbRole: string): Role {
  // A session stored before the roles went uppercase still holds `admin`.
  // TODO: drop the `toUpperCase` after 2026-10-08 - when no in localStorage
  const role = String(dbRole).toUpperCase();

  if (!(ROLES_INHERITANCE as Record<string, string[]>)[role]) {
    throw new Error(`Unknown role ${dbRole}`);
  }

  return role as Role;
}

const ROLES_INHERITANCE: Record<Role, Role[]> = {
  ADMIN: ['ADMIN', 'PROVIDER', 'CUSTOMER'],
  PROVIDER: ['PROVIDER', 'CUSTOMER'],
  CUSTOMER: ['CUSTOMER'],
};

export interface TokenDto {
  idToken: string;
  refreshToken: string;
}

export interface AuthSessionDto extends TokenDto {
  role: Role;
}

export interface FullAuthSessionDto extends AuthSessionDto {
  impersonated?: AuthSessionDto;
}

export type RoleCheckMode = 'active' | 'any' | 'impersonated' | 'main';
export type HasRoleOptions = {
  /**
   * Which session to check the role against.
   *
   * - `active` (default) — the currently active session (impersonated if
   *   impersonating, otherwise main).
   * - `any` — passes if either main or impersonated session has the role.
   * - `impersonated` — only the impersonated session.
   * - `main` — only the main (signed-in) session; ignores impersonation.
   *   Use when an admin-only capability must stay available even while the
   *   admin is impersonating another user.
   */
  mode?: RoleCheckMode;
};
